import React, { useState, useEffect, useRef } from 'react';
import { Package, GraduationCap, AlertTriangle, Plus, ClipboardList, Map, BarChart3, ChevronRight, X, Check, Bell } from 'lucide-react';
import { useAuth } from './context/AuthContext';
import { API_URL, apiFetch } from './config/api';

function HomeProfile({ onNavigate }) {
  const { tenant, tenants, switchTenant, csrfToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [alertScope, setAlertScope] = useState('own'); // 'own' or 'all'
  const [showPinnedAnnouncement, setShowPinnedAnnouncement] = useState(null);
  const [userAnnouncements, setUserAnnouncements] = useState([]);
  const [showAllAnnouncementsModal, setShowAllAnnouncementsModal] = useState(false);
  const [announcementFilter, setAnnouncementFilter] = useState('all'); // 'all', 'unread', 'read'

  // Notification bell state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null);

  // Track if we're handling popstate to avoid double history.back()
  const isHandlingPopState = useRef(false);

  // Helper to close modal and clean up history
  const closeAnnouncementModal = () => {
    if (!isHandlingPopState.current) window.history.back();
    setShowAnnouncementModal(false);
  };

  const closeAllAnnouncementsModal = () => {
    if (!isHandlingPopState.current) window.history.back();
    setShowAllAnnouncementsModal(false);
  };

  const closePinnedAnnouncement = () => {
    if (!isHandlingPopState.current) window.history.back();
    setShowPinnedAnnouncement(null);
  };

  // Handle back button for modals
  useEffect(() => {
    const handlePopState = () => {
      if (showAnnouncementModal) {
        isHandlingPopState.current = true;
        setShowAnnouncementModal(false);
        isHandlingPopState.current = false;
        return;
      }
      if (showAllAnnouncementsModal) {
        isHandlingPopState.current = true;
        setShowAllAnnouncementsModal(false);
        isHandlingPopState.current = false;
        return;
      }
      if (showPinnedAnnouncement) {
        isHandlingPopState.current = true;
        setShowPinnedAnnouncement(null);
        isHandlingPopState.current = false;
        return;
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [showAnnouncementModal, showAllAnnouncementsModal, showPinnedAnnouncement]);

  // Push history state when modals open
  useEffect(() => {
    if (showAnnouncementModal) {
      window.history.pushState({ modal: 'announcement' }, '');
    }
  }, [showAnnouncementModal]);

  useEffect(() => {
    if (showAllAnnouncementsModal) {
      window.history.pushState({ modal: 'allAnnouncements' }, '');
    }
  }, [showAllAnnouncementsModal]);

  useEffect(() => {
    if (showPinnedAnnouncement) {
      window.history.pushState({ modal: 'pinnedAnnouncement' }, '');
    }
  }, [showPinnedAnnouncement]);

  useEffect(() => {
    if (tenant) {
      fetchDashboardData();
      checkPinnedAnnouncements();
      loadUserAnnouncements();
      loadNotifications();
    }
  }, [tenant, alertScope]); // Reload when tenant or scope changes

  // Load notifications for bell
  const loadNotifications = async () => {
    try {
      const response = await apiFetch(`${API_URL}/notifications/list.php?limit=14`);
      const result = await response.json();
      if (result.success) {
        setNotifications(result.notifications);
        setUnreadCount(result.unread_count);
      }
    } catch (err) {
      if (!err.sessionExpired) {
        console.error('Failed to load notifications:', err);
      }
    }
  };

  // Toggle notification dropdown (don't auto-mark as read)
  const handleBellClick = () => {
    setShowNotifications(!showNotifications);
  };

  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle notification click - mark as read and navigate
  const handleNotificationClick = async (notification) => {
    // Mark this notification as read
    if (!notification.is_read) {
      try {
        await apiFetch(`${API_URL}/notifications/list.php`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken || ''
          },
          body: JSON.stringify({
            action: 'mark_read',
            notification_ids: [notification.id]
          })
        });

        // Update local state
        setNotifications(prev => prev.map(n =>
          n.id === notification.id ? { ...n, is_read: true } : n
        ));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (err) {
        console.error('Failed to mark notification as read:', err);
      }
    }

    setShowNotifications(false);

    // Navigate based on notification type
    if (notification.url) {
      const hash = notification.url.replace('/taskmanager/#', '').replace('/taskmanager/', '');
      if (hash && onNavigate) {
        onNavigate(hash);
      }
    }
  };

  // Load user announcements with read status
  const loadUserAnnouncements = async () => {
    try {
      const response = await apiFetch(`${API_URL}/announcements/list.php`);
      const result = await response.json();
      if (result.success) {
        setUserAnnouncements(result.announcements);
      }
    } catch (err) {
      if (!err.sessionExpired) {
        console.error('Failed to load user announcements:', err);
      }
    }
  };

  // Check for unread pinned announcements on mount
  const checkPinnedAnnouncements = async () => {
    try {
      const response = await apiFetch(`${API_URL}/announcements/unread-pinned.php`);
      const result = await response.json();
      if (result.success && result.announcements.length > 0) {
        setShowPinnedAnnouncement(result.announcements[0]); // Show first unread pinned
      }
    } catch (err) {
      if (!err.sessionExpired) {
        console.error('Failed to check pinned announcements:', err);
      }
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await apiFetch(`${API_URL}/dashboard-home.php?scope=${alertScope}`);

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const result = await response.json();
      if (result.success) {
        setData(result);
      } else {
        setError(result.error || 'Unknown error');
      }
    } catch (err) {
      // Session expired is handled by apiFetch
      if (!err.sessionExpired) {
        console.error('Dashboard fetch error:', err);
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnnouncementClick = (announcement) => {
    setSelectedAnnouncement(announcement);
    setShowAnnouncementModal(true);
  };

  // Handle acknowledging pinned announcement
  const handleAcknowledgePinned = async () => {
    if (!showPinnedAnnouncement) return;

    try {
      const response = await fetch(`${API_URL}/announcements/mark-read.php`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ announcement_id: showPinnedAnnouncement.id })
      });

      const result = await response.json();
      if (result.success) {
        closePinnedAnnouncement();
        // Reload announcements to update read status
        loadUserAnnouncements();
        // Check if there are more pinned announcements
        checkPinnedAnnouncements();
      }
    } catch (err) {
      console.error('Failed to acknowledge pinned announcement:', err);
    }
  };

  const handleAcknowledgeAnnouncement = async (announcementId) => {
    try {
      const response = await fetch(`${API_URL}/announcements/mark-read.php`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ announcement_id: announcementId })
      });

      const result = await response.json();
      if (result.success) {
        // Refresh announcements to update read status
        loadUserAnnouncements();
        closeAnnouncementModal();
      }
    } catch (err) {
      console.error('Failed to acknowledge announcement:', err);
    }
  };

  // Mark announcement as read (for OK buttons)
  const handleMarkAsRead = async (announcementId) => {
    try {
      const response = await fetch(`${API_URL}/announcements/mark-read.php`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ announcement_id: announcementId })
      });

      const result = await response.json();
      if (result.success) {
        loadUserAnnouncements();
      }
    } catch (err) {
      console.error('Failed to mark announcement as read:', err);
    }
  };

  const handleQuickAction = (actionId) => {
    if (!onNavigate) return;

    switch(actionId) {
      case 'tasks':
        onNavigate('tasks'); // Navigate to ProjectsList (tasks view)
        break;
      case 'add_device':
        // Open devices view with add device modal (will be implemented in DeviceList)
        onNavigate('devices', { openAddModal: true });
        break;
      case 'map':
        // Navigate to devices with map view enabled
        onNavigate('devices', { showMap: true });
        break;
      case 'reports':
        onNavigate('reports');
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingBottom: '100px'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid #334155',
          borderTop: '4px solid #FFEB3B',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0f172a',
        padding: '20px',
        paddingBottom: '100px'
      }}>
        <div style={{
          background: '#1e293b',
          border: '1px solid #ef4444',
          borderRadius: '12px',
          padding: '20px',
          color: 'white',
          textAlign: 'center'
        }}>
          <AlertTriangle size={48} color="#ef4444" style={{ marginBottom: '12px' }} />
          <p>Virhe ladattaessa tietoja: {error}</p>
          <button
            onClick={fetchDashboardData}
            style={{
              marginTop: '16px',
              padding: '12px 24px',
              background: '#FFEB3B',
              color: '#0f172a',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Yritä uudelleen
          </button>
        </div>
      </div>
    );
  }

  const { user, tenant: dataTenant, stats, announcements, alerts, quick_actions } = data;

  const getAlertStyles = (type) => {
    switch(type) {
      case 'critical':
        return { bg: 'rgba(239, 68, 68, 0.1)', border: '#ef4444', text: '#ef4444' };
      case 'warning':
        return { bg: 'rgba(255, 235, 59, 0.1)', border: '#FFEB3B', text: '#FFEB3B' };
      case 'success':
        return { bg: 'rgba(16, 185, 129, 0.1)', border: '#10b981', text: '#10b981' };
      default:
        return { bg: '#1e293b', border: '#334155', text: '#94a3b8' };
    }
  };

  const getAnnouncementStyles = (type) => {
    switch(type) {
      case 'important':
        return { bg: 'rgba(239, 68, 68, 0.1)', border: '#ef4444' };
      case 'tip':
        return { bg: 'rgba(59, 130, 246, 0.1)', border: '#3b82f6' };
      case 'celebration':
        return { bg: 'rgba(168, 85, 247, 0.1)', border: '#a855f7' };
      default:
        return { bg: '#1e293b', border: '#334155' };
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0f172a',
      paddingBottom: '100px'
    }}>
      {/* Welcome Section */}
      <div style={{ padding: '20px 16px 12px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <h1 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'white',
              marginBottom: '4px'
            }}>
              Tervetuloa 👋
            </h1>
            <p style={{
              fontSize: '14px',
              color: '#94a3b8',
              marginBottom: '2px'
            }}>
              {data?.user?.name || user.name}
            </p>
            <p style={{
              fontSize: '12px',
              color: '#64748b'
            }}>
              {data?.user?.role || user.role} • {tenant?.name || 'Loading...'}
            </p>
          </div>

          {/* Right side: Tenant Switcher + Notification Bell */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
            {/* Tenant Switcher - only show if user has multiple tenants */}
            {tenants && tenants.length > 1 && (
              <select
                value={tenant?.id || ''}
                onChange={(e) => switchTenant(parseInt(e.target.value))}
                style={{
                  padding: '8px 12px',
                  background: '#1e293b',
                  color: 'white',
                  border: '1px solid #334155',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  minWidth: '120px'
                }}
              >
                {tenants.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            )}

            {/* Notification Bell */}
            <div ref={notificationRef} style={{ position: 'relative' }}>
              <button
                onClick={handleBellClick}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '6px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  transition: 'background 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = '#1e293b'}
                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <Bell size={22} color="#94a3b8" />
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    width: '8px',
                    height: '8px',
                    background: '#ef4444',
                    borderRadius: '50%'
                  }} />
                )}
              </button>

              {/* Notification Dropdown */}
              {showNotifications && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '8px',
                  width: '320px',
                  maxHeight: '400px',
                  background: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
                  zIndex: 1000,
                  overflow: 'hidden'
                }}>
                  {/* Dropdown Header */}
                  <div style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #334155',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <span style={{ color: 'white', fontWeight: '600', fontSize: '14px' }}>
                      Ilmoitukset
                    </span>
                    {notifications.length > 0 && (
                      <span style={{ color: '#64748b', fontSize: '12px' }}>
                        {notifications.length} kpl
                      </span>
                    )}
                  </div>

                  {/* Notifications List */}
                  <div style={{ maxHeight: '340px', overflowY: 'auto' }}>
                    {notifications.length === 0 ? (
                      <div style={{
                        padding: '32px 16px',
                        textAlign: 'center',
                        color: '#64748b',
                        fontSize: '14px'
                      }}>
                        Ei ilmoituksia
                      </div>
                    ) : (
                      notifications.map((notification) => (
                        <div
                          key={notification.id}
                          onClick={() => handleNotificationClick(notification)}
                          style={{
                            padding: '12px 16px',
                            borderBottom: '1px solid #334155',
                            cursor: 'pointer',
                            transition: 'background 0.2s, opacity 0.2s',
                            background: notification.is_read ? 'transparent' : 'rgba(59, 130, 246, 0.1)',
                            opacity: notification.is_read ? 0.5 : 1
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.background = '#242d3f';
                            e.currentTarget.style.opacity = '1';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.background = notification.is_read ? 'transparent' : 'rgba(59, 130, 246, 0.1)';
                            e.currentTarget.style.opacity = notification.is_read ? '0.5' : '1';
                          }}
                        >
                          <div style={{
                            fontSize: '13px',
                            fontWeight: notification.is_read ? '500' : '600',
                            color: 'white',
                            marginBottom: '4px'
                          }}>
                            {notification.title}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#94a3b8',
                            marginBottom: '4px',
                            lineHeight: '1.4',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {notification.body}
                          </div>
                          <div style={{
                            fontSize: '11px',
                            color: '#64748b'
                          }}>
                            {notification.time_ago}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{
        padding: '0 16px 20px 16px',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '12px'
      }}>
        {/* Kohteet - Clickable */}
        <button
          onClick={() => onNavigate && onNavigate('devices')}
          style={{
            background: '#1e293b',
            border: '1px solid #334155',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.borderColor = '#FFEB3B';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.borderColor = '#334155';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <Package size={32} color="#FFEB3B" style={{ margin: '0 auto 8px' }} />
          <div style={{
            fontSize: '28px',
            fontWeight: 'bold',
            color: 'white',
            marginBottom: '4px'
          }}>
            {stats.devices}
          </div>
          <div style={{
            fontSize: '13px',
            color: '#94a3b8'
          }}>
            Kohteet
          </div>
        </button>

        {/* Kortit - Clickable */}
        <button
          onClick={() => onNavigate && onNavigate('training')}
          style={{
            background: '#1e293b',
            border: '1px solid #334155',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.borderColor = '#FFEB3B';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.borderColor = '#334155';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <GraduationCap size={32} color="#FFEB3B" style={{ margin: '0 auto 8px' }} />
          <div style={{
            fontSize: '28px',
            fontWeight: 'bold',
            color: 'white',
            marginBottom: '4px'
          }}>
            {stats.certifications}
          </div>
          <div style={{
            fontSize: '13px',
            color: '#94a3b8'
          }}>
            Kortit
          </div>
        </button>

        {/* Tehtävät - Clickable */}
        <button
          onClick={() => onNavigate && onNavigate('tasks')}
          style={{
            background: '#1e293b',
            border: '1px solid #334155',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.borderColor = '#FFEB3B';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.borderColor = '#334155';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <ClipboardList size={32} color="#FFEB3B" style={{ margin: '0 auto 8px' }} />
          <div style={{
            fontSize: '28px',
            fontWeight: 'bold',
            color: 'white',
            marginBottom: '4px'
          }}>
            {stats.tasks || 0}
          </div>
          <div style={{
            fontSize: '13px',
            color: '#94a3b8'
          }}>
            Tehtävät
          </div>
        </button>
      </div>

      {/* Alerts Section - MOVED TO TOP */}
      <div style={{ padding: '0 16px 20px 16px' }}>
        <div style={{
          background: '#1e293b',
          border: '1px solid #334155',
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            background: '#242d3f',
            borderBottom: '1px solid #334155',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertTriangle size={20} color="#FFEB3B" />
              <h2 style={{
                fontSize: '16px',
                fontWeight: 'bold',
                color: 'white',
                margin: 0
              }}>
                HUOMIOT JA HÄLYTYKSET
              </h2>
            </div>

            {/* Filter toggle - only show for admin/owner */}
            {data?.user?.is_admin && (
              <div style={{
                display: 'flex',
                gap: '4px',
                background: '#1e293b',
                borderRadius: '6px',
                padding: '4px'
              }}>
                <button
                  onClick={() => setAlertScope('own')}
                  style={{
                    padding: '4px 12px',
                    background: alertScope === 'own' ? '#3b82f6' : 'transparent',
                    color: alertScope === 'own' ? 'white' : '#94a3b8',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Omat
                </button>
                <button
                  onClick={() => setAlertScope('all')}
                  style={{
                    padding: '4px 12px',
                    background: alertScope === 'all' ? '#3b82f6' : 'transparent',
                    color: alertScope === 'all' ? 'white' : '#94a3b8',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Kaikki
                </button>
              </div>
            )}
          </div>

          {/* Alerts */}
          <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {alerts.map((alert, index) => {
              const styles = getAlertStyles(alert.type);
              const isClickable = alert.action || alert.type === 'expiring_certifications' || alert.type === 'pending_document' || alert.type === 'assigned_task';

              return (
                <div
                  key={index}
                  onClick={() => {
                    // Navigate to correct view based on alert type
                    if (alert.type === 'expiring_training' || alert.type === 'expired_training' || alert.type === 'pending_training') {
                      // Training alerts -> Koulutukset tab
                      onNavigate('training', { showExpiring: true, initialView: 'my-trainings' });
                    } else if (alert.type === 'expiring_certifications' || alert.type === 'expired_certifications') {
                      // Certification alerts -> Kortit tab
                      onNavigate('training', { showExpiring: true, initialView: 'my-certifications' });
                    } else if (alert.type === 'pending_document') {
                      // QMS document waiting approval -> QMS view
                      onNavigate('qms', { initialTab: 'documents' });
                    } else if (alert.type === 'assigned_task') {
                      // Assigned task -> Projects/Tasks view
                      onNavigate('tasks');
                    } else if (alert.action) {
                      // Handle other action types
                      onNavigate(alert.action);
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    background: styles.bg,
                    border: `1px solid ${styles.border}`,
                    borderRadius: '8px',
                    cursor: isClickable ? 'pointer' : 'default',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    if (isClickable) {
                      e.currentTarget.style.background = styles.bg === 'rgba(239, 68, 68, 0.1)' ? 'rgba(239, 68, 68, 0.2)' :
                                                         styles.bg === 'rgba(255, 235, 59, 0.1)' ? 'rgba(255, 235, 59, 0.2)' :
                                                         styles.bg === 'rgba(16, 185, 129, 0.1)' ? 'rgba(16, 185, 129, 0.2)' : '#242d3f';
                    }
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = styles.bg;
                  }}
                >
                  <span style={{ fontSize: '20px', flexShrink: 0 }}>
                    {alert.icon}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      fontSize: '14px',
                      color: 'white',
                      margin: 0
                    }}>
                      {alert.message}
                    </p>
                  </div>
                  {(alert.action || isClickable) && (
                    <ChevronRight size={20} color="#94a3b8" style={{ flexShrink: 0 }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Company Announcements */}
      {userAnnouncements && userAnnouncements.length > 0 && (
        <div style={{ padding: '0 16px 20px 16px' }}>
          <div style={{
            background: '#1e293b',
            border: '1px solid #334155',
            borderRadius: '12px',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              background: '#242d3f',
              borderBottom: '1px solid #334155',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '20px' }}>📢</span>
              <h2 style={{
                fontSize: '16px',
                fontWeight: 'bold',
                color: 'white',
                margin: 0
              }}>
                YRITYKSEN TIEDOTTEET
              </h2>
            </div>

            {/* Announcements - 3 column grid on desktop */}
            <div
              className="announcements-grid"
              style={{
                padding: '12px',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '12px'
              }}
            >
              {userAnnouncements.slice(0, 6).map((announcement) => {
                const styles = getAnnouncementStyles(announcement.type);
                return (
                  <div
                    key={announcement.id}
                    onClick={() => handleAnnouncementClick(announcement)}
                    style={{
                      background: styles.bg,
                      border: `1px solid ${styles.border}`,
                      borderRadius: '12px',
                      padding: '16px',
                      opacity: announcement.is_read ? 0.7 : 1,
                      cursor: 'pointer',
                      transition: 'transform 0.2s, opacity 0.2s, box-shadow 0.2s',
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: '180px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {/* Header with icon and status */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '12px'
                    }}>
                      <span style={{ fontSize: '32px' }}>
                        {announcement.icon}
                      </span>
                      {announcement.is_read && (
                        <span style={{
                          fontSize: '11px',
                          background: '#10b981',
                          color: '#ffffff',
                          padding: '2px 8px',
                          borderRadius: '4px'
                        }}>
                          ✓ Luettu
                        </span>
                      )}
                    </div>

                    {/* Title */}
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: 'white',
                      marginBottom: '8px',
                      margin: 0
                    }}>
                      {announcement.title}
                    </h4>

                    {/* Message preview */}
                    <p style={{
                      fontSize: '13px',
                      color: '#cbd5e1',
                      marginBottom: '12px',
                      lineHeight: '1.5',
                      flex: 1,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {announcement.message.length > 120
                        ? announcement.message.substring(0, 120) + '...'
                        : announcement.message}
                    </p>

                    {/* Footer */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: 'auto',
                      paddingTop: '12px',
                      borderTop: '1px solid rgba(255,255,255,0.1)'
                    }}>
                      <span style={{
                        fontSize: '11px',
                        color: '#64748b'
                      }}>
                        {new Date(announcement.created_at).toLocaleDateString('fi-FI')}
                      </span>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {!announcement.is_read && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkAsRead(announcement.id);
                            }}
                            style={{
                              background: '#10b981',
                              border: 'none',
                              color: '#ffffff',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              padding: '6px 12px',
                              borderRadius: '6px'
                            }}
                          >
                            ✓ OK
                          </button>
                        )}
                        <ChevronRight size={18} color="#94a3b8" />
                      </div>
                    </div>
                  </div>
                );
              })}

            </div>

            {/* View All Button */}
            <div style={{ padding: '0 12px 12px 12px' }}>
              <button
                onClick={() => setShowAllAnnouncementsModal(true)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#242d3f',
                  border: '1px solid #334155',
                  borderRadius: '8px',
                  color: '#FFEB3B',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#2d3748';
                  e.currentTarget.style.borderColor = '#FFEB3B';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = '#242d3f';
                  e.currentTarget.style.borderColor = '#334155';
                }}
              >
                Näytä kaikki tiedotteet <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div style={{ padding: '0 16px 20px 16px' }}>
        <div style={{
          background: '#1e293b',
          border: '1px solid #334155',
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            background: '#242d3f',
            borderBottom: '1px solid #334155',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '20px' }}>🎯</span>
            <h2 style={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: 'white',
              margin: 0
            }}>
              PIKAVALINNAT
            </h2>
          </div>

          {/* Actions Grid */}
          <div style={{
            padding: '12px',
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '12px'
          }}>
            {quick_actions.map((action) => {
              const icons = {
                'tasks': ClipboardList,
                'add_device': Plus,
                'map': Map,
                'reports': BarChart3
              };
              const Icon = icons[action.id] || ClipboardList;

              return (
                <button
                  key={action.id}
                  onClick={() => handleQuickAction(action.id)}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '20px',
                    background: '#242d3f',
                    border: '1px solid #334155',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#2d3748';
                    e.currentTarget.style.borderColor = '#FFEB3B';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = '#242d3f';
                    e.currentTarget.style.borderColor = '#334155';
                  }}
                >
                  <Icon size={28} color="#FFEB3B" />
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'white'
                  }}>
                    {action.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Announcement Modal */}
      {showAnnouncementModal && selectedAnnouncement && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: '#1e293b',
            borderRadius: '16px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            border: `2px solid ${getAnnouncementStyles(selectedAnnouncement.type).border}`
          }}>
            {/* Header */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #334155',
              display: 'flex',
              alignItems: 'start',
              justifyContent: 'space-between',
              gap: '12px'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '24px',
                  marginBottom: '8px'
                }}>
                  {selectedAnnouncement.icon}
                </div>
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: 'white',
                  margin: 0,
                  marginBottom: '8px'
                }}>
                  {selectedAnnouncement.title}
                </h2>
                <p style={{
                  fontSize: '13px',
                  color: '#64748b',
                  margin: 0
                }}>
                  {selectedAnnouncement.time_ago}
                </p>
              </div>
              <button
                onClick={closeAnnouncementModal}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div style={{
              padding: '20px',
              color: '#cbd5e1',
              fontSize: '15px',
              lineHeight: '1.6'
            }}>
              {selectedAnnouncement.message}
            </div>

            {/* Footer */}
            <div style={{
              padding: '20px',
              borderTop: '1px solid #334155',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              {selectedAnnouncement.requires_acknowledgment ? (
                <>
                  <button
                    onClick={closeAnnouncementModal}
                    style={{
                      padding: '12px 24px',
                      background: '#334155',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Sulje
                  </button>
                  <button
                    onClick={() => handleAcknowledgeAnnouncement(selectedAnnouncement.id)}
                    style={{
                      padding: '12px 24px',
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Check size={18} />
                    Olen lukenut ja ymmärtänyt
                  </button>
                </>
              ) : (
                <button
                  onClick={closeAnnouncementModal}
                  style={{
                    padding: '12px 24px',
                    background: '#FFEB3B',
                    color: '#0f172a',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Sulje
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pinned Announcement Popup */}
      {showPinnedAnnouncement && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#1e293b',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '80vh',
            overflowY: 'auto',
            border: '3px solid #FFEB3B',
            boxShadow: '0 20px 60px rgba(255, 235, 59, 0.3)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '48px', marginBottom: '1rem' }}>
                {showPinnedAnnouncement.icon}
              </div>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#FFEB3B',
                marginBottom: '0.5rem'
              }}>
                {showPinnedAnnouncement.title}
              </h2>
              {showPinnedAnnouncement.type && (
                <div style={{
                  display: 'inline-block',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  background: (() => {
                    const typeColors = {
                      important: '#ef4444',
                      news: '#3b82f6',
                      tip: '#FFEB3B',
                      celebration: '#10b981'
                    };
                    return typeColors[showPinnedAnnouncement.type] || '#3b82f6';
                  })(),
                  color: showPinnedAnnouncement.type === 'tip' ? '#0f172a' : '#ffffff'
                }}>
                  {showPinnedAnnouncement.type === 'important' && '⚠️ Tärkeä'}
                  {showPinnedAnnouncement.type === 'news' && '📰 Uutinen'}
                  {showPinnedAnnouncement.type === 'tip' && '💡 Vinkki'}
                  {showPinnedAnnouncement.type === 'celebration' && '🎉 Juhla'}
                </div>
              )}
            </div>

            <div style={{
              background: '#0f172a',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <p style={{
                color: '#e2e8f0',
                fontSize: '16px',
                lineHeight: '1.6',
                margin: 0,
                whiteSpace: 'pre-wrap'
              }}>
                {showPinnedAnnouncement.message}
              </p>
            </div>

            {showPinnedAnnouncement.created_by_name && (
              <div style={{
                fontSize: '12px',
                color: '#94a3b8',
                marginBottom: '1.5rem',
                textAlign: 'center'
              }}>
                Lähettäjä: {showPinnedAnnouncement.created_by_name} • {new Date(showPinnedAnnouncement.created_at).toLocaleDateString('fi-FI')}
              </div>
            )}

            <div style={{ textAlign: 'center' }}>
              <button
                onClick={handleAcknowledgePinned}
                style={{
                  padding: '0.75rem 2rem',
                  background: '#FFEB3B',
                  color: '#0f172a',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(255, 235, 59, 0.4)',
                  transition: 'transform 0.2s'
                }}
                onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
              >
                ✓ OK, Ymmärsin
              </button>
            </div>
          </div>
        </div>
      )}

      {/* All Announcements Modal */}
      {showAllAnnouncementsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#1e293b',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#FFEB3B', margin: 0 }}>
                Kaikki tiedotteet
              </h2>
              <button
                onClick={closeAllAnnouncementsModal}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  fontSize: '24px',
                  padding: '0.5rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Filter Tabs */}
            <div style={{
              display: 'flex',
              gap: '0.5rem',
              marginBottom: '1.5rem',
              borderBottom: '2px solid #334155'
            }}>
              <button
                onClick={() => setAnnouncementFilter('all')}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: announcementFilter === 'all' ? '#FFEB3B' : 'transparent',
                  color: announcementFilter === 'all' ? '#0f172a' : '#94a3b8',
                  border: 'none',
                  borderBottom: announcementFilter === 'all' ? '2px solid #FFEB3B' : 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Kaikki ({userAnnouncements.length})
              </button>
              <button
                onClick={() => setAnnouncementFilter('unread')}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: announcementFilter === 'unread' ? '#FFEB3B' : 'transparent',
                  color: announcementFilter === 'unread' ? '#0f172a' : '#94a3b8',
                  border: 'none',
                  borderBottom: announcementFilter === 'unread' ? '2px solid #FFEB3B' : 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Lukemattomat ({userAnnouncements.filter(a => !a.is_read).length})
              </button>
              <button
                onClick={() => setAnnouncementFilter('read')}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: announcementFilter === 'read' ? '#FFEB3B' : 'transparent',
                  color: announcementFilter === 'read' ? '#0f172a' : '#94a3b8',
                  border: 'none',
                  borderBottom: announcementFilter === 'read' ? '2px solid #FFEB3B' : 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Luetut ({userAnnouncements.filter(a => a.is_read).length})
              </button>
            </div>

            {/* Filtered Announcements */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {userAnnouncements
                .filter(announcement => {
                  if (announcementFilter === 'unread') return !announcement.is_read;
                  if (announcementFilter === 'read') return announcement.is_read;
                  return true;
                })
                .map(announcement => {
                  const typeColors = {
                    important: { border: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' },
                    news: { border: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)' },
                    tip: { border: '#FFEB3B', bg: 'rgba(255, 235, 59, 0.1)' },
                    celebration: { border: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' }
                  };
                  const colors = typeColors[announcement.type] || typeColors.news;

                  return (
                    <div
                      key={announcement.id}
                      style={{
                        borderLeft: `4px solid ${colors.border}`,
                        background: colors.bg,
                        padding: '1.5rem',
                        borderRadius: '8px',
                        opacity: announcement.is_read ? 0.7 : 1
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'start', gap: '1rem' }}>
                        <span style={{ fontSize: '32px' }}>{announcement.icon}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                            <h4 style={{ fontSize: '18px', fontWeight: '700', color: '#ffffff', margin: 0 }}>
                              {announcement.title}
                            </h4>
                            {announcement.is_pinned && (
                              <span style={{
                                background: '#FFEB3B',
                                color: '#0f172a',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '600'
                              }}>
                                📌 Kiinnitetty
                              </span>
                            )}
                            {announcement.is_read ? (
                              <span style={{
                                background: '#10b981',
                                color: '#ffffff',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '600'
                              }}>
                                ✓ Luettu {announcement.read_at && `• ${new Date(announcement.read_at).toLocaleDateString('fi-FI')}`}
                              </span>
                            ) : (
                              <button
                                onClick={() => handleMarkAsRead(announcement.id)}
                                style={{
                                  background: '#10b981',
                                  border: 'none',
                                  color: '#ffffff',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  padding: '0.25rem 0.75rem',
                                  borderRadius: '4px'
                                }}
                              >
                                ✓ Merkitse luetuksi
                              </button>
                            )}
                          </div>
                          <p style={{
                            color: '#e2e8f0',
                            fontSize: '14px',
                            margin: '0 0 0.5rem 0',
                            whiteSpace: 'pre-wrap',
                            lineHeight: '1.6'
                          }}>
                            {announcement.message}
                          </p>
                          <div style={{ display: 'flex', gap: '1rem', fontSize: '12px', color: '#94a3b8' }}>
                            <span>📅 {new Date(announcement.created_at).toLocaleDateString('fi-FI')}</span>
                            {announcement.created_by_name && (
                              <span>👤 {announcement.created_by_name}</span>
                            )}
                            {announcement.expires_at && !announcement.is_expired && (
                              <span>⏰ Vanhenee: {new Date(announcement.expires_at).toLocaleDateString('fi-FI')}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

              {userAnnouncements.filter(announcement => {
                if (announcementFilter === 'unread') return !announcement.is_read;
                if (announcementFilter === 'read') return announcement.is_read;
                return true;
              }).length === 0 && (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                  {announcementFilter === 'unread' && 'Ei lukemattomia tiedotteita'}
                  {announcementFilter === 'read' && 'Ei luettuja tiedotteita'}
                  {announcementFilter === 'all' && 'Ei tiedotteita'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HomeProfile;